import React, { useEffect, useState } from "react";
import { User, ShieldCheck, Bell, Tag as TagIcon, Users2, Plus, UserX, Mail } from "lucide-react";
import JeaCard from "@/components/jea/JeaCard";
import JeaButton from "@/components/jea/JeaButton";
import PillButton from "@/components/jea/PillButton";
import Skeleton from "@/components/jea/Skeleton";
import { demoClient, useDemo } from "@/pages/demo/DemoShell";
import { cn } from "@/lib/utils";

const TABS = [
  { key: "profile",       label: "Profile",             Icon: User },
  { key: "security",      label: "Security",            Icon: ShieldCheck },
  { key: "notifications", label: "Notification Prefs",  Icon: Bell },
  { key: "tags",          label: "Tags",                Icon: TagIcon },
  { key: "team",          label: "Team",                Icon: Users2 },
];

const DemoSettingsPage = () => {
  const demo = useDemo();
  const [tab, setTab] = useState("profile");
  const [tags, setTags] = useState([]);
  const [prefs, setPrefs] = useState(null);
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState({ tags: true, prefs: true, team: true });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await demoClient.get("/demo/tags");
        setTags(data.tags || []);
      } finally { setLoading((s) => ({ ...s, tags: false })); }
    })();
    (async () => {
      try {
        const { data } = await demoClient.get("/demo/settings/notification-prefs");
        setPrefs(data);
      } finally { setLoading((s) => ({ ...s, prefs: false })); }
    })();
    (async () => {
      try {
        const { data } = await demoClient.get("/demo/team/members");
        setTeam(data);
      } finally { setLoading((s) => ({ ...s, team: false })); }
    })();
  }, []);

  const nudge = (feat) => () => demo?.openSignUp(feat);

  return (
    <div className="space-y-6" data-testid="demo-settings-page">
      <div>
        <div className="jea-caption">Account</div>
        <h1 className="text-[28px] font-bold text-white">Settings</h1>
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="demo-settings-nav">
        {TABS.map((t) => (
          <PillButton
            key={t.key}
            active={tab === t.key}
            onClick={() => setTab(t.key)}
            data-testid={`demo-settings-tab-${t.key}`}
          >
            {t.label}
          </PillButton>
        ))}
      </div>

      <div className="pt-2" data-tour="settings-panel">
        {tab === "profile" && (
          <JeaCard className="space-y-4" data-testid="demo-settings-profile">
            <Row label="Full Name"   value="Demo User" />
            <Row label="Email"       value="demo@judgment-ai.local" />
            <Row label="Account"     value="PRO · Annual" />
            <Row label="Time Zone"   value="America/Los_Angeles" />
            <JeaButton variant="outline-pink" onClick={nudge("editing your profile")}>Save Changes</JeaButton>
          </JeaCard>
        )}

        {tab === "security" && (
          <JeaCard className="space-y-4" data-testid="demo-settings-security">
            <Row label="Password"        value="•••••••••" />
            <Row label="Two-Factor Auth" value="Enabled · Email" />
            <Row label="Last Login"      value="2 hours ago from San Francisco, CA" />
            <div className="flex flex-wrap gap-2">
              <JeaButton variant="outline-pink" onClick={nudge("changing your password")}>Change Password</JeaButton>
              <JeaButton variant="outline-neutral" onClick={nudge("configuring 2FA")}>Configure 2FA</JeaButton>
            </div>
          </JeaCard>
        )}

        {tab === "notifications" && (
          loading.prefs ? <Skeleton className="h-40" /> : (
            <div className="space-y-4" data-testid="demo-settings-notifications">
              <JeaCard>
                <h2 className="text-white font-bold text-[15px] mb-3">Channels</h2>
                <div className="space-y-2">
                  {Object.entries(prefs?.channels || {}).map(([key, val]) => (
                    <div key={key} className="flex items-center justify-between bg-bg-tertiary rounded-[8px] p-3">
                      <div>
                        <div className="text-white text-[13px] font-semibold uppercase tracking-[0.06em]">{key}</div>
                        {val.number && (
                          <div className="text-text-secondary text-[11px] jea-mono">{val.number}</div>
                        )}
                      </div>
                      <span className={cn(
                        "text-[10px] font-bold tracking-[0.08em] uppercase px-2 py-1 rounded-full",
                        val.enabled ? "bg-success/15 text-success" : "bg-bg-primary text-text-muted"
                      )}>
                        {val.enabled ? (val.verified ? "Verified" : "Pending") : "Off"}
                      </span>
                    </div>
                  ))}
                </div>
              </JeaCard>
              <JeaCard>
                <h2 className="text-white font-bold text-[15px] mb-3">Events</h2>
                <div className="space-y-2">
                  {Object.entries(prefs?.events || {}).map(([key, val]) => (
                    <div key={key} className="grid grid-cols-[1fr_auto_auto] items-center gap-3 bg-bg-tertiary rounded-[8px] p-3">
                      <div className="text-white text-[13px] capitalize">{key.replace(/_/g, " ")}</div>
                      <FlagPill on={val.email}>Email</FlagPill>
                      <FlagPill on={val.sms}>SMS</FlagPill>
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex justify-end">
                  <JeaButton variant="outline-pink" size="sm" onClick={nudge("updating notification preferences")} data-testid="demo-settings-save-prefs">
                    Save Preferences
                  </JeaButton>
                </div>
              </JeaCard>
            </div>
          )
        )}

        {tab === "tags" && (
          loading.tags ? <Skeleton className="h-40" /> : (
            <JeaCard data-testid="demo-settings-tags">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-white font-bold text-[15px]">Portfolio Tags ({tags.length})</h2>
                <JeaButton size="sm" className="gap-2" onClick={nudge("creating custom tags")} data-testid="demo-settings-tag-add">
                  <Plus size={14} /> Create Tag
                </JeaButton>
              </div>
              <div className="flex flex-wrap gap-2">
                {tags.map((t) => (
                  <div
                    key={t.id}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[12px] font-semibold"
                    style={{ background: `${t.color}22`, color: t.color, border: `1px solid ${t.color}55` }}
                    data-testid={`demo-tag-${t.slug}`}
                  >
                    {t.name}
                  </div>
                ))}
              </div>
            </JeaCard>
          )
        )}

        {tab === "team" && (
          loading.team ? <Skeleton className="h-40" /> : (
            <div className="space-y-4" data-testid="demo-settings-team">
              <JeaCard>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-white font-bold text-[15px]">Team · {1 + (team?.members?.length || 0)} members</h2>
                  <JeaButton size="sm" className="gap-2" onClick={nudge("inviting team members")} data-testid="demo-team-invite">
                    <Plus size={14} /> Invite Team Member
                  </JeaButton>
                </div>
                <div className="space-y-2">
                  <MemberRow member={team?.owner} isOwner nudge={nudge} />
                  {(team?.members || []).map((m) => (
                    <MemberRow key={m.id} member={m} nudge={nudge} />
                  ))}
                </div>
              </JeaCard>

              {(team?.pending_invites || []).length > 0 && (
                <JeaCard>
                  <h3 className="text-white font-bold text-[14px] mb-3">Pending Invites</h3>
                  <div className="space-y-2">
                    {(team?.pending_invites || []).map((inv) => (
                      <div key={inv.id} className="grid grid-cols-[auto_1fr_auto] items-center gap-3 bg-bg-tertiary rounded-[8px] p-3">
                        <Mail size={14} className="text-accent-pink" />
                        <div className="min-w-0">
                          <div className="text-white text-[13px] font-semibold truncate">{inv.email}</div>
                          <div className="text-text-secondary text-[11px]">Role: {inv.role} · Invited {new Date(inv.invited_at).toLocaleDateString()}</div>
                        </div>
                        <button type="button" className="text-danger text-[12px] font-semibold hover:underline" onClick={nudge("managing invites")}>
                          Revoke
                        </button>
                      </div>
                    ))}
                  </div>
                </JeaCard>
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="grid grid-cols-[180px_1fr] items-center gap-4">
    <div className="jea-caption">{label}</div>
    <div className="text-white text-[14px]">{value}</div>
  </div>
);

const FlagPill = ({ on, children }) => (
  <span className={cn(
    "text-[10px] font-bold tracking-[0.06em] uppercase px-2 py-1 rounded-full",
    on ? "bg-accent-pink/20 text-accent-pink" : "bg-bg-primary text-text-muted"
  )}>
    {children}
  </span>
);

const MemberRow = ({ member, isOwner, nudge }) => {
  if (!member) return null;
  return (
    <div className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-3 bg-bg-tertiary rounded-[8px] p-3">
      <div className="w-9 h-9 rounded-full bg-accent-pink/20 border border-accent-pink/40 flex items-center justify-center text-accent-pink font-bold text-[13px]">
        {(member.full_name || "?")[0]}
      </div>
      <div className="min-w-0">
        <div className="text-white text-[13px] font-semibold truncate">{member.full_name}</div>
        <div className="text-text-secondary text-[11px] jea-mono truncate">{member.email}</div>
      </div>
      <span className="text-[10px] font-bold tracking-[0.08em] uppercase px-2 py-1 rounded-full bg-accent-pink/15 text-accent-pink">
        {(member.role || "").replace("_", " ")}
      </span>
      {isOwner ? (
        <span className="text-text-muted text-[11px] font-semibold">—</span>
      ) : (
        <button type="button" onClick={nudge("removing team members")} className="text-text-secondary hover:text-danger" title="Remove">
          <UserX size={14} />
        </button>
      )}
    </div>
  );
};

export default DemoSettingsPage;
